import { useRouter } from 'next/router'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'

import HomeTabIcon from '../assets/icons/sidebar-layout/home-tab.svg'
import LearnChordsTabIcon from '../assets/icons/sidebar-layout/learn-chords-tab.svg'
import MasteringTabIcon from '../assets/icons/sidebar-layout/mastering-tab.svg'
import MinimizeSidebarIcon from '../assets/icons/sidebar-layout/minimize-sidebar.svg'
import StudioTabIcon from '../assets/icons/sidebar-layout/studio-tab.svg'
import TemplatesTabIcon from '../assets/icons/sidebar-layout/templates-tab.svg'
import TrackSeparationTabIcon from '../assets/icons/sidebar-layout/track-separation-tab.svg'
import { SIDEBAR_TAB_KEYS, TSidebarTab, TSidebarTabKeys } from '../components/sidebar-layout/SidebarLayout'
import useSizes from '../hooks/useSizes'
import useStorageState from '../hooks/useStorageState'
import routes from '../utils/routes'
import { windowOpen } from '../utils/stringUtils'
import { TSongTemplate } from '../utils/types'
import { useInternationalization } from './InternationalizationContext'

const toolRouteIntoKey: { [key: string]: TSidebarTabKeys } = {
  [routes.GENERATOR]: SIDEBAR_TAB_KEYS.HOME,
  [routes.STUDIO]: SIDEBAR_TAB_KEYS.STUDIO,
  [routes.TEMPLATES]: SIDEBAR_TAB_KEYS.TEMPLATES,
  [routes.TRACK_SEPARATION]: SIDEBAR_TAB_KEYS.TRACK_SEPARATION,
  [routes.MASTERING]: SIDEBAR_TAB_KEYS.MASTERING,
  [routes.LEARN_CHORDS]: SIDEBAR_TAB_KEYS.LEARN_CHORDS,
}
const toolKeyIntoRoute = Object.fromEntries(Object.entries(toolRouteIntoKey).map(([route, key]) => [key, route]))

type FetchType = {
  // General
  pageLoading: boolean
  setPageLoading: (v: boolean) => void
  // Sidebar
  activeSidebarTab: TSidebarTab
  activeSidebarTabKey: TSidebarTabKeys
  sidebarLocked: boolean
  setSidebarLocked: (v: boolean) => void
  sidebarOpen: boolean
  setSidebarOpen: (v: boolean) => void
  // Tabs
  homeTab: TSidebarTab
  toolTabs: TSidebarTab[][]
  minimizeTab: TSidebarTab
  // tools
  studioProjectId: string
  trackSeparationProjectId: string
  masteringProjectId: string
  currentProjectId: string
  // functions
  handleOpenProject: (projectId?: string, tabKey?: TSidebarTabKeys) => Promise<void>
  handleCloseProject: () => Promise<void>
  handleOpenPage: (url: string, locale?: string) => Promise<void>
  // Templates
  selectedTemplate: TSongTemplate | null
  setSelectedTemplate: (v: TSongTemplate | null) => void
}

export const GlobalToolConfigContext = React.createContext<FetchType>({
  // General
  pageLoading: false,
  setPageLoading: () => {},
  // Sidebar
  activeSidebarTab: {} as any,
  activeSidebarTabKey: 'HOME',
  sidebarLocked: false,
  setSidebarLocked: () => {},
  sidebarOpen: false,
  setSidebarOpen: () => {},
  // Tabs
  homeTab: {
    key: 'HOME',
    icon: undefined,
    name: '',
  },
  toolTabs: [],
  minimizeTab: {
    key: 'HOME',
    icon: undefined,
    name: '',
  },
  // tools
  studioProjectId: '',
  trackSeparationProjectId: '',
  masteringProjectId: '',
  currentProjectId: '',
  // functions
  handleOpenProject: () => new Promise(() => {}),
  handleCloseProject: () => new Promise(() => {}),
  handleOpenPage: () => new Promise(() => {}),
  // Templates
  selectedTemplate: null,
  setSelectedTemplate: () => {},
})

export const GlobalToolConfigProvider = ({
  children,
  tool = '',
  projectId = '',
}: {
  children: ReactNode
  tool?: string
  projectId?: string
}) => {
  const router = useRouter()
  const { isMobile } = useSizes()
  const { getText } = useInternationalization()

  const STRINGS = getText('SidebarLayout')

  // General
  const [pageLoading, setPageLoading] = useState(true)
  // Sidebar
  const [activeSidebarTabKey, setSidebarActiveTabKey] = useState<TSidebarTabKeys>(SIDEBAR_TAB_KEYS.HOME)
  const [sidebarLocked, setSidebarLocked] = useStorageState(false, 'sidebar-locked')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Tools
  const [studioProjectId, setStudioProjectId] = useState('')
  const [trackSeparationProjectId, setTrackSeparationProjectId] = useState('')
  const [masteringProjectId, setMasteringProjectId] = useState('')
  // Templates
  const [selectedTemplate, setSelectedTemplate] = useState<TSongTemplate | null>(null)

  const currentProjectId = useMemo(() => {
    switch (activeSidebarTabKey) {
      case SIDEBAR_TAB_KEYS.STUDIO:
        return studioProjectId
      case SIDEBAR_TAB_KEYS.TRACK_SEPARATION:
        return trackSeparationProjectId
      case SIDEBAR_TAB_KEYS.MASTERING:
        return masteringProjectId
      default:
        return ''
    }
  }, [activeSidebarTabKey, studioProjectId, trackSeparationProjectId, masteringProjectId])

  useEffect(() => {
    setPageLoading(true)

    const newActiveSidebarTabKey = tool ? toolRouteIntoKey[tool] : SIDEBAR_TAB_KEYS.HOME
    setSidebarActiveTabKey(newActiveSidebarTabKey)

    if (newActiveSidebarTabKey === SIDEBAR_TAB_KEYS.STUDIO) setStudioProjectId(projectId)
    if (newActiveSidebarTabKey === SIDEBAR_TAB_KEYS.TRACK_SEPARATION) setTrackSeparationProjectId(projectId)
    if (newActiveSidebarTabKey === SIDEBAR_TAB_KEYS.MASTERING) setMasteringProjectId(projectId)
  }, [tool, projectId])

  const homeTab: TSidebarTab = {
    key: SIDEBAR_TAB_KEYS.HOME,
    icon: <HomeTabIcon />,
    name: STRINGS.home,
    isSoon: false,
    component: () => import('../components/sidebar-layout/tabs/HomeTab'),
    href: '/',
  }
  const toolTabs: TSidebarTab[][] = [
    [
      {
        key: SIDEBAR_TAB_KEYS.STUDIO,
        icon: <StudioTabIcon />,
        name: STRINGS.studio,
        isSoon: false,
        component: () => import('../components/sidebar-layout/tabs/StudioTab'),
        href: '/studio',
      },
      {
        key: SIDEBAR_TAB_KEYS.TEMPLATES,
        icon: <TemplatesTabIcon />,
        name: STRINGS.templates,
        isSoon: false,
        component: () => import('../components/sidebar-layout/tabs/TemplatesTab'),
        href: '/templates',
      },
    ],
    [
      {
        key: SIDEBAR_TAB_KEYS.TRACK_SEPARATION,
        icon: <TrackSeparationTabIcon />,
        name: STRINGS.trackSeparation,
        isSoon: true,
        component: () => import('../components/sidebar-layout/tabs/TrackSeparationTab'),
        href: '/track-separation',
      },
      {
        key: SIDEBAR_TAB_KEYS.MASTERING,
        icon: <MasteringTabIcon />,
        name: STRINGS.mastering,
        isSoon: true,
        component: () => import('../components/sidebar-layout/tabs/MasteringTab'),
        href: '/mastering',
      },
      {
        key: SIDEBAR_TAB_KEYS.LEARN_CHORDS,
        icon: <LearnChordsTabIcon />,
        name: STRINGS.learnChords,
        isSoon: true,
        component: () => import('../components/sidebar-layout/tabs/LearnChordsTab'),
        href: '/learn-chords',
      },
    ],
  ]
  const minimizeTab: TSidebarTab = {
    key: SIDEBAR_TAB_KEYS.MINIMIZE_SIDEBAR,
    icon: <MinimizeSidebarIcon />,
    name: STRINGS.minimizeSidebar,
    onClick: () => setSidebarLocked((v) => !v),
  }

  const activeSidebarTab = useMemo(
    () => [homeTab, ...toolTabs.flat(), minimizeTab].find((t) => t.key === activeSidebarTabKey) || homeTab,
    [activeSidebarTabKey],
  )

  const handleOpenProject = async (projectIdParam?: string, tabKeyParam?: TSidebarTabKeys) => {
    const tabKey = tabKeyParam || activeSidebarTabKey
    const newProjectId = projectIdParam || ''

    const route = toolKeyIntoRoute[tabKey]

    if (isMobile) setSidebarOpen(false)
    if (route) {
      if (newProjectId) {
        windowOpen(`${route}/${newProjectId}`, '_parent')
      } else if (projectId) {
        windowOpen(route, '_parent')
      } else {
        handleOpenPage(route)
      }
    }
  }
  const handleCloseProject = async () => {
    const route = toolKeyIntoRoute[activeSidebarTabKey]

    if (isMobile) setSidebarOpen(false)
    if (route) windowOpen(route, '_parent')
  }

  const handleOpenPage = async (url: string, locale?: string) => {
    await router.push(url, undefined, { locale: locale === 'en' ? '' : locale, shallow: true }).then(() => {
      if (locale) window.location.reload()
    })
  }

  return (
    <GlobalToolConfigContext.Provider
      value={{
        // General
        pageLoading,
        setPageLoading,
        // Sidebar
        activeSidebarTab,
        activeSidebarTabKey,
        sidebarLocked,
        setSidebarLocked,
        sidebarOpen,
        setSidebarOpen,
        // Tabs
        homeTab,
        toolTabs,
        minimizeTab,
        // tools
        studioProjectId,
        trackSeparationProjectId,
        masteringProjectId,
        currentProjectId,
        // functions
        handleOpenProject,
        handleCloseProject,
        handleOpenPage,
        // Templates
        selectedTemplate,
        setSelectedTemplate,
      }}
    >
      {children}
    </GlobalToolConfigContext.Provider>
  )
}

export const useGlobalToolConfig = () => React.useContext<FetchType>(GlobalToolConfigContext)
